import { useRef, useState, useSyncExternalStore } from 'react';
import { X } from '../../icons';
import { createTeam, type TeamDto } from '../../api/project-teams';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import { GROUP_COLORS } from './TaskGroupCreateModal';
import './SpaceCreateModal.css';

interface SpaceCreateModalProps {
  onClose: () => void;
  onCreated: (team: TeamDto) => void;
}

/**
 * SpaceCreateModal (2026-08-19, 시안 "스페이스 생성")
 *
 * 프로젝트 홈의 [＋ 새 스페이스 만들기] 가 여는 모달. 시안대로 **색상 + 이름 두 가지**만
 * 받고, 아래에 "스페이스란?" 안내를 붙인다. 만들고 나면 그 스페이스 안(프로젝트 목록)으로 간다.
 *
 * 멤버 초대가 함께 붙은 큰 모달(TeamCreateInviteModal, AW-003)은 그대로 살아 있다 —
 * 팀 화면(/project/teams)의 [새 팀] 이 그쪽을 연다. 여기서 스페이스를 만든 뒤에도
 * 멤버는 스페이스 설정에서 언제든 초대할 수 있다.
 *
 * ⚠ 시안의 안내 두 번째 줄("회사 멤버라면 누구나 접근")은 우리 동작과 다르다 —
 * 스페이스 멤버는 명시적으로 초대해야 하고, 만든 사람이 마스터가 된다. 화면이 사실과
 * 다른 말을 하지 않도록 그 줄만 우리 규칙으로 바꿔 적었다.
 */
export function SpaceCreateModal({ onClose, onCreated }: SpaceCreateModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const nameRef = useRef<HTMLInputElement | null>(null);

  const [name, setName] = useState('');
  const [color, setColor] = useState(GROUP_COLORS[6]!); // 시안의 분홍 스와치
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeClose(onClose, submitting);

  async function submit() {
    const n = name.trim();
    // 비활성 버튼 대신 이유를 말한다(다른 생성 화면과 같은 규칙).
    if (!n) {
      setError(tProject('project.space.err_name'));
      nameRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      onCreated(await createTeam({ name: n, color }));
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tProject('project.team.create.err_save')}`);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="pj-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.space.create_title')}
    >
      <div ref={trapRef} className="pj-modal spc-modal">
        <header className="spc-head">
          <h2>{tProject('project.space.create_title')}</h2>
          <button type="button" className="spc-x" onClick={onClose} disabled={submitting} aria-label={tProject('project.btn.close')} data-tip={tProject('project.btn.close')}>
            <X size={16} />
          </button>
        </header>

        <label className="spc-label" htmlFor="spc-name">{tProject('project.space.color_and_name')}</label>
        <div className="spc-row">
          <div className="spc-swatch-wrap">
            <button
              type="button"
              className="spc-swatch"
              style={{ background: color }}
              aria-label={tProject('project.create.color_pick')}
              aria-expanded={paletteOpen}
              onClick={() => setPaletteOpen((v) => !v)}
            />
            {paletteOpen && (
              <div className="spc-palette" role="radiogroup" aria-label={tProject('project.create.color_pick')}>
                {GROUP_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={c === color}
                    aria-label={c}
                    className={`spc-dot${c === color ? ' spc-dot--on' : ''}`}
                    style={{ background: c }}
                    onClick={() => { setColor(c); setPaletteOpen(false); }}
                  />
                ))}
              </div>
            )}
          </div>
          <input
            id="spc-name"
            ref={nameRef}
            className="spc-input"
            aria-invalid={error != null && !name.trim()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tProject('project.space.name_ph')}
            maxLength={100}
            autoFocus
            disabled={submitting}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
          />
        </div>

        <section className="spc-about">
          <h3>{tProject('project.space.about_title')}</h3>
          <p>{tProject('project.space.about_1')}</p>
          <p>{tProject('project.space.about_2')}</p>
        </section>

        {error && <div className="pj-modal-error" role="alert">{error}</div>}

        <footer className="spc-foot">
          <button type="button" className="spc-cancel" onClick={onClose} disabled={submitting}>
            {tProject('project.btn.cancel')}
          </button>
          <button type="button" className="spc-submit" onClick={() => void submit()} disabled={submitting}>
            {submitting ? tProject('project.btn.saving') : tProject('project.create.confirm')}
          </button>
        </footer>
      </div>
    </div>
  );
}
